import React, { useMemo, useState } from 'react';
import { Search, ExternalLink, BadgeCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import courses from '../data/courses.json';

const coverMap = {
  'course-1.png': () => require('../assets/course-1.png'),
  'course-2.png': () => require('../assets/course-2.png'),
  'course-3.png': () => require('../assets/course-3.png'),
};

const categoryFilters = ['All Courses', 'Business', 'Tech', 'AI'];

const resolveCover = (cover) => {
  if (!cover || typeof cover !== 'string') return null;

  const trimmed = cover.trim();
  if (!trimmed) return null;

  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  const loader = coverMap[trimmed];
  if (!loader) {
    try {
      return require(`../assets/${trimmed}`);
    } catch {
      return null;
    }
  }

  try {
    return loader();
  } catch {
    return null;
  }
};

const FeaturedCourses = () => {
  const [activeCategory, setActiveCategory] = useState('All Courses');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return courses.filter((c) => {
      const inCategory =
        activeCategory === 'All Courses' ||
        c.categories.some((tag) => tag.toLowerCase() === activeCategory.toLowerCase());

      const inQuery =
        !q ||
        c.title.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q) ||
        c.creator.toLowerCase().includes(q);

      return inCategory && inQuery;
    });
  }, [activeCategory, query]);

  return (
    <section id="courses" className="bg-white">
      <div className="mx-3 md:mx-10 lg:mx-16 py-14 md:py-20">
        <div className="text-center">
          <h2 className="text-4xl md:text-4xl font-inter font-medium text-acBlack leading-[1.05]">
            Featured Business &amp; Tech
            <span className="relative inline-block ml-2">
              <span className="absolute inset-0 bg-pink-400/80 rounded-full -rotate-1" />
              <span className="relative px-3">Courses</span>
            </span>
          </h2>
          <p className="mt-3 text-sm md:text-base text-acGray font-inter">
            Curated, outcome-driven programs from trusted creators.
          </p>
        </div>

        <div className="mt-8 md:mt-10 flex flex-col md:flex-row items-stretch md:items-center justify-center gap-3 md:gap-4">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {categoryFilters.map((label) => {
              const isActive = label === activeCategory;
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => setActiveCategory(label)}
                  className={
                    (isActive
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-acBlack hover:bg-gray-200') +
                    ' px-5 py-2 rounded-full text-sm font-inter transition'
                  }
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div className="w-full md:w-[360px]">
            <div className="flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2">
              <Search size={18} className="text-gray-500" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search"
                className="bg-transparent w-full outline-none text-sm font-inter text-acBlack placeholder:text-gray-500"
              />
            </div>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((course) => (
            <div key={course.id} className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
              <div className="relative h-44 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-700">
                {resolveCover(course.cover) && (
                  <img
                    src={resolveCover(course.cover)}
                    alt={course.title}
                    className="absolute inset-0 w-full h-full object-cover z-0"
                  />
                )}
                {resolveCover(course.cover) && (
                  <div className="absolute inset-0 bg-black/45 z-10" />
                )}

                <div className="absolute top-3 left-3 bg-orange-500 text-white text-xs font-inter font-semibold px-3 py-1 rounded-full z-20">
                  Recommended
                </div>
                <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/15 flex items-center justify-center z-20">
                  <ExternalLink size={16} className="text-white" />
                </div>
              </div>

              <div className="p-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="inline-flex items-center gap-2 bg-gray-50 border border-gray-100 text-acBlack text-xs font-inter px-3 py-1 rounded-full">
                    <BadgeCheck size={14} className="text-blue-600" />
                    {course.flag}
                  </div>
                </div>

                <h3 className="mt-4 text-lg font-inter font-semibold text-acBlack leading-snug">
                  {course.title}
                </h3>
                <p className="mt-2 text-sm text-gray-600 font-inter leading-relaxed">
                  {course.description}
                </p>

                {/* <div className="mt-4 text-sm text-gray-700 font-inter">
                  By:{' '}
                  <span className="font-semibold text-acBlack">{course.creator || '—'}</span>
                </div> */}

                <div className="mt-3 flex flex-wrap gap-2">
                  {course.categories.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs font-inter bg-gray-100 text-acBlack px-3 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                  <span className="text-xs font-inter bg-blue-50 text-blue-700 px-3 py-1 rounded-full">
                    {course.outcome}
                  </span>
                </div>

                <div className="mt-5 flex items-center justify-between">
                  <div className="text-lg font-inter font-semibold text-acBlack">{course.price}</div>
                  {course.path ? (
                    <Link
                      to={course.path}
                      className="px-5 py-2 rounded-full bg-acBlack text-white text-sm font-inter font-semibold hover:opacity-90 transition"
                    >
                      View Course
                    </Link>
                  ) : course.url ? (
                    <a
                      href={course.url}
                      target="_blank"
                      rel="noreferrer"
                      className="px-5 py-2 rounded-full bg-acBlack text-white text-sm font-inter font-semibold hover:opacity-90 transition"
                    >
                      View Course
                    </a>
                  ) : (
                    <button
                      type="button"
                      disabled
                      className="px-5 py-2 rounded-full bg-gray-200 text-gray-500 text-sm font-inter font-semibold cursor-not-allowed"
                      title="Add a course URL in src/data/courses.json"
                    >
                      View Course
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedCourses;
